import { X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { StepComponentProps } from "../step-config-file/types";

interface InsightItem {
  label: string | any;
  value: string | number | any;
}

const InsightTable = <T extends InsightItem>({
  data,
  title,
}: {
  data: T[];
  title: string;
}) => (
  <>
    <h6 className="mt-5 text-base font-medium">{title}</h6>
    <Table>
      <TableBody>
        {data.map((item, index) => (
          <TableRow key={index} className="border-0">
            <TableCell className="border-0 p-0">{item.label}</TableCell>
            <TableCell className="border-0 p-0 text-right">
              {item.value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </>
);

interface ClimateInsightsProps {
  address: string;
  climateInsights: InsightItem[];
  buildingDetails: InsightItem[];
  season: string;
  people: number;
  setCloseFeatureCard: (close: boolean) => void;
}

const ClimateInsightsComponent: React.FC<ClimateInsightsProps> = ({
  address,
  climateInsights,
  buildingDetails,
  setCloseFeatureCard,
}) => {
  const initialPosition = {
    x: Math.min(window.innerWidth * 0.7, window.innerWidth - 700),
    y: window.innerHeight * 0.1,
  };
  const [position, setPosition] = useState(initialPosition);
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={(_, data) => setPosition({ x: data.x, y: data.y })}
    >
      <div ref={nodeRef}>
        <Card className="flex w-100">
          <CardContent className="p-4 pb-0">
            <CardHeader className="flex cursor-move flex-row items-center justify-between p-0">
              <CardTitle className="text-sm font-medium">{address}</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                aria-label="close"
                className="size-7"
                onClick={() => setCloseFeatureCard(true)}
              >
                <X className="size-4" />
              </Button>
            </CardHeader>

            <InsightTable data={climateInsights} title="Climate Insights" />
            <Separator className="mt-2.5" />
            <InsightTable data={buildingDetails} title="Building Details" />
          </CardContent>
        </Card>
      </div>
    </Draggable>
  );
};

interface ClimateProperties {
  climate: number;
  ci: number;
  solar: string;
  pool: string;
  net_let_area: number;
  floors: number;
  area: number;
  electric_consumption: number | string;
  gas_consumption: number | string;
  elec_net_area: number | string;
  gas_net_area: number | string;
  electric_CO2_annually: number | string;
  electric_CO2_season: number | string;
  gas_CO2_season: number | string;
  gas_CO2_annually: number | string;
  total_CO2_annually: number | string;
  total_CO2_annually_per_m2: number | string;
  emitted_co2: string;
  emitted_co2_sqm: string;
  volume: number;
  roof_material: string;
  roof_slope: string;
  building_height: string;
  eave_height: string;
  roof_shape: string;
  roof_colour: string;
}

const getPropertyValue = (
  properties: Record<string, any>,
  key: string,
  people: number,
  season: string,
): number | string => {
  return (
    Number(properties[`${key}_${people}_${season}`]).toFixed(2) || "No data"
  );
};

const getAnnualPropertyValue = (
  properties: Record<string, any>,
  key: string,
  people: number,
): number | string => {
  return Number(properties[`${key}_${people}_Annual`]).toFixed(2) || "No data";
};

export const App2 = (props: StepComponentProps) => {
  const { properties } = props.attributes;

  // @ts-ignore
  const { people, season } = props.formState;

  const climateProperties = useMemo<ClimateProperties>(
    () => ({
      climate: properties.climate,
      ci: properties.ci,
      solar: properties.solar,
      pool: properties.pool,
      net_let_area: properties.net_let_area,
      floors: properties.floors,
      area: properties.area,
      electric_consumption: getPropertyValue(
        properties,
        "elec_con",
        people,
        season,
      ),
      gas_consumption: getPropertyValue(properties, "gas_con", people, season),
      elec_net_area: getPropertyValue(
        properties,
        "elec_net_area",
        people,
        season,
      ),
      gas_net_area: getPropertyValue(
        properties,
        "gas_net_area",
        people,
        season,
      ),
      electric_CO2_season: getPropertyValue(
        properties,
        "elec_co2_kg_yr",
        people,
        season,
      ),
      electric_CO2_annually: getAnnualPropertyValue(
        properties,
        "elec_co2_kg_yr",
        people,
      ),
      gas_CO2_season: getPropertyValue(
        properties,
        "gas_co2_kg_yr",
        people,
        season,
      ),
      gas_CO2_annually: getAnnualPropertyValue(
        properties,
        "gas_co2_kg_yr",
        people,
      ),
      total_CO2_annually: getAnnualPropertyValue(
        properties,
        "total_co2_kg_yr",
        people,
      ),
      total_CO2_annually_per_m2: getAnnualPropertyValue(
        properties,
        "total_co2_kg_m2_yr",
        people,
      ),
      emitted_co2: properties.emitted_c02_kg_yr,
      emitted_co2_sqm: properties.emitted_c02_kg_m2_yr,
      volume: properties.volume,
      roof_material: properties.primary_roof_material,
      roof_slope: properties.roof_slope,
      building_height: properties.roof_height,
      eave_height: properties.eave_height,
      roof_shape: properties.roof_shape,
      roof_colour: properties.roof_colour,
    }),
    [properties, people, season],
  );

  const climateInsights: InsightItem[] = useMemo(
    () => [
      { label: "Climate Zone", value: climateProperties.climate },
      {
        label: "Carbon Intensity Elec",
        value: `${climateProperties.ci} g CO₂/kW`,
      },
      {
        label: "Net Lettable Area",
        value: (
          <>
            {climateProperties.net_let_area} m<sup>2</sup>
          </>
        ),
      },
      { label: "Has Solar Panels?", value: climateProperties.solar },
      { label: "Has Swimming Pool?", value: climateProperties.pool },
      {
        label: "Area",
        value: (
          <>
            {climateProperties.area} m<sup>2</sup>
          </>
        ),
      },
      {
        label: (
          <>
            Emitted CO<sub>2</sub>
          </>
        ),
        value: `${Number(climateProperties.emitted_co2).toFixed(2)} Kg`,
      },
      {
        label: (
          <>
            Emitted CO<sub>2</sub> sqm
          </>
        ),
        value: `${Number(climateProperties.emitted_co2_sqm).toFixed(2)} Kg`,
      },
    ],
    [climateProperties],
  );

  const buildingDetails: InsightItem[] = useMemo(
    () => [
      {
        label: "Volume",
        value: (
          <>
            {climateProperties.volume} m<sup>3</sup>
          </>
        ),
      },
      { label: "Number of Floors", value: climateProperties.floors },
      {
        label: "Net Lettable Area",
        value: (
          <>
            {climateProperties.net_let_area} m<sup>2</sup>
          </>
        ),
      },
      { label: "Roof Material", value: climateProperties.roof_material },
      { label: "Roof Slope", value: `${climateProperties.roof_slope} Deg` },
      {
        label: "Building Height",
        value: `${climateProperties.building_height} m`,
      },
      { label: "Eave Height", value: `${climateProperties.eave_height} m` },
      { label: "Roof Shape", value: climateProperties.roof_shape },
      { label: "Roof Colour", value: climateProperties.roof_colour },
    ],
    [climateProperties],
  );

  const [closeFeatureCard, setCloseFeatureCard] = useState(false);

  useEffect(() => {
    setCloseFeatureCard(false);
  }, [props.attributes]);

  if (props.attributes.layer.id == props.layer_name && !closeFeatureCard) {
    return (
      <ClimateInsightsComponent
        address={props.attributes.properties.address}
        climateInsights={climateInsights}
        buildingDetails={buildingDetails}
        season={season}
        people={people}
        setCloseFeatureCard={setCloseFeatureCard}
      />
    );
  }

  return null;
};
export default ClimateInsightsComponent;
